import { deg2rad } from "../math/angles";
import { integrateWithCentroid } from "../math/integrate";
import type { Section } from "../geometry/section";
import type { Backfill, Water } from "../types/input";
import type { WarningCode } from "../warnings";
import { thetaFor } from "../seismic/coefficients";
import { activeCoefficient } from "./coefficients";

export interface DiagramSegment {
  index: number; layerIndex: number; label: string;
  zTop: number; zBot: number;          // sanal düzlem üstünden (y_c) derinlik
  yTop: number; yBot: number;          // mutlak kot
  phi: number; c: number; delta: number; gammaEff: number; submerged: boolean;
  theta: number; Kstatic: number; Ktotal: number; Kdyn: number; branch: "a" | "b";
  sigmaVTop: number;                   // segment üstündeki düşey gerilme (q dahil)
  face?: number;                       // back-face yönteminde kademe sırası (üstten, 1'den)
}
export interface SegmentForce { index: number; Fstatic: number; zStatic: number; Fdyn: number; zDyn: number; delta: number; clipped: boolean }
/** İtki düzlemi: varsayılan sanal düzlem (section.psi, planeTop.y → baseY); back-face yönteminde her kademe için ayrı verilir. */
export interface PressurePlane { psi: number; yC: number; yBot: number }
/**
 * kvOnTotal: TBDY 2018 Denk. 16.23 harfiyen — Pt = ½·K_at·(1 ± kv)·(γ*·H² + …): (1 ± kv) toplam itkiye (statik kısım dahil) uygulanır;
 * dinamik artım K_at·(1 ± kv) − K_as (≥ 0, toplam itki statikten küçük alınmaz). false: yalnız artım (K_at − K_as)·(1 ± kv) (eski).
 */
export interface DiagramInput { section: Section; backfill: Backfill; water: Water; surcharge: number; kh: number; kvFactor: number; warn: (code: WarningCode) => void; plane?: PressurePlane; kvOnTotal?: boolean }

const TOL = 1e-9;

export function buildSegments(inp: DiagramInput): DiagramSegment[] {
  const { section, backfill, water, surcharge, kh, kvFactor, warn, kvOnTotal } = inp;
  const beta = deg2rad(backfill.slopeAngle);
  const plane: PressurePlane = inp.plane ?? { psi: section.psi, yC: section.planeTop.y, yBot: section.baseY };
  const yC = plane.yC;
  const baseY = plane.yBot;
  const hw = water.backLevel;                      // mutlak kot (taban altından), null = su yok
  const gw = water.gammaWater;

  // Tabaka alt sınırları (mutlak kot), duvar tepesinden (yTop) aşağı
  const bounds: number[] = [];
  let y = section.yTop;
  for (const L of backfill.layers) { y -= L.thickness; bounds.push(y); }
  const lastBound = bounds[bounds.length - 1]!;
  if (lastBound > baseY + TOL) warn("LAYERS_EXTENDED");

  // Kırılma kotları
  const cuts = new Set<number>([yC, baseY]);
  // Son tabaka tabana kadar uzatılır; alt sınırı kesme kotu değildir.
  for (const b of bounds.slice(0, -1)) if (b > baseY + TOL && b < yC - TOL) cuts.add(b);
  if (hw !== null && hw > baseY + TOL && hw < yC - TOL) cuts.add(hw);
  const ys = [...cuts].sort((a, b) => b - a);   // azalan

  const layerAt = (yMid: number): number => {
    for (let i = 0; i < bounds.length; i++) if (yMid > bounds[i]! - TOL) return i;
    return backfill.layers.length - 1;
  };

  const segments: DiagramSegment[] = [];
  let sigmaV = surcharge;
  for (let i = 0; i + 1 < ys.length; i++) {
    const yTop = ys[i]!, yBot = ys[i + 1]!;
    const yMid = (yTop + yBot) / 2;
    const li = layerAt(yMid);
    const L = backfill.layers[li]!;
    const phi = deg2rad(L.phi);
    if (beta >= phi - TOL) throw new Error("Dolgu eğimi β ≥ φ: Coulomb çözümü tanımsız (SLOPE_GE_PHI)");
    const submerged = hw !== null && yMid < hw;
    let gammaEff = L.gamma;
    let thetaMultiplier = 1;
    if (submerged) {
      if (L.gammaSat === undefined) throw new Error("Su altındaki tabaka için doygun birim hacim ağırlığı gerekli (GAMMA_SAT_REQUIRED)");
      gammaEff = L.gammaSat - gw;
      thetaMultiplier = backfill.permeability === "impermeable" ? L.gammaSat / (L.gammaSat - gw) : L.gamma / (L.gammaSat - gw);
    }
    const deltaMax = (2 * phi) / 3;
    let delta = deltaMax;
    if (backfill.wallFrictionAngle === "beta") delta = beta;   // Rankine: düşey düzlemde δ = β (kinematik, sınırlanmaz)
    else if (backfill.wallFrictionAngle !== "two-thirds-phi") {
      const req = deg2rad(backfill.wallFrictionAngle);
      if (req > deltaMax + TOL) { delta = deltaMax; warn("DELTA_CAPPED"); } else delta = req;
    }
    const theta = kh > 0 ? thetaFor(kh, kvFactor, thetaMultiplier) : 0;
    const base = { phi, delta, beta, psi: plane.psi };
    const Ks = activeCoefficient({ ...base, theta: 0 });
    const Kt = theta > 0 ? activeCoefficient({ ...base, theta }) : Ks;
    if (Kt.branch === "b") warn("MO_LIMIT");
    segments.push({
      index: segments.length, layerIndex: li, label: L.name ?? `Tabaka-${li + 1}`,
      zTop: yC - yTop, zBot: yC - yBot, yTop, yBot,
      phi, c: L.c, delta, gammaEff, submerged,
      theta, Kstatic: Ks.K, Ktotal: Kt.K,
      // Kdyn·(1 ± kv) = dinamik artım; Denk. 16.23 harfiyen: K_at·(1 ± kv) − K_as → Kdyn = K_at − K_as/(1 ± kv)
      Kdyn: kvOnTotal && kh > 0 && kvFactor > 0 ? Math.max(0, Kt.K - Ks.K / kvFactor) : Kt.K - Ks.K, branch: Kt.branch,
      sigmaVTop: sigmaV,
    });
    sigmaV += gammaEff * (yTop - yBot);
  }
  if (backfill.layers.length > 1) warn("LAYERED_APPROX");
  if (backfill.layers.some((L) => L.c > 0)) warn("COHESION_APPROX");
  return segments;
}

export function integrateSegments(segments: DiagramSegment[], _surcharge: number, kvFactor: number, n = 256): SegmentForce[] {
  return segments.map((s) => {
    const Kac = 2 * Math.sqrt(s.Kstatic);
    const sigmaV = (z: number): number => s.sigmaVTop + s.gammaEff * (z - s.zTop);
    const pStatic = (z: number): number => Math.max(0, s.Kstatic * sigmaV(z) - s.c * Kac);
    const pDyn = (z: number): number => Math.max(0, s.Kdyn * kvFactor * sigmaV(z));
    const st = integrateWithCentroid(pStatic, s.zTop, s.zBot, n);
    const dy = integrateWithCentroid(pDyn, s.zTop, s.zBot, n);
    const clipped = s.Kstatic * s.sigmaVTop - s.c * Kac < -TOL;
    return { index: s.index, Fstatic: st.F, zStatic: st.zBar, Fdyn: dy.F, zDyn: dy.zBar, delta: s.delta, clipped };
  });
}
